/**
 * PinLoginForm — school code + student number + 4-digit PIN [PHASE-5 2026-10].
 *
 * For primary and middle-stage pupils. The school code is remembered on the
 * device (only the code: never the number, never the PIN), so a child on the
 * family tablet types two short numbers. Arabic-Indic digits are accepted and
 * converted (lib/pinLogin.js). There is no way to submit without a PIN.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff, School, Hash, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { studentPinsApi } from '../services/apiService';
import apiErrorMessage from '../lib/apiError';
import {
  PIN_LENGTH,
  digitsOnly,
  buildPinLoginPayload,
  pinLoginErrorKey,
  loadRememberedSchoolCode,
  rememberSchoolCode,
  forgetSchoolCode,
} from '../lib/pinLogin';

const PinLoginForm = ({ onLogin, t }) => {
  const [remembered, setRemembered] = useState(() => loadRememberedSchoolCode());
  const [schoolCode, setSchoolCode] = useState(remembered);
  const [studentNumber, setStudentNumber] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lockMessage, setLockMessage] = useState('');

  const changeSchool = () => {
    forgetSchoolCode();
    setRemembered('');
    setSchoolCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { payload, error } = buildPinLoginPayload({ schoolCode, studentNumber, pin });
    if (error) {
      toast.error(t(error));
      return;
    }
    setLoading(true);
    setLockMessage('');
    try {
      const data = await studentPinsApi.login(payload);
      rememberSchoolCode(payload.school_number);
      setPin('');
      toast.success(t('success'));
      onLogin(data.access_token, data.user, data.expires_in);
    } catch (err) {
      setPin('');
      const { key, params } = pinLoginErrorKey(err);
      const message = key ? t(key, params) : apiErrorMessage(err, t('error'));
      // A lock (429) or an expired school (403) stays on screen, not only in a toast.
      if ([403, 429].includes(err?.response?.status)) setLockMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = 'h-12 border-2 focus:border-teal-600 rounded-xl text-lg tracking-widest text-center font-mono';

  return (
    <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off" data-testid="pin-login-form">
      <p className="text-sm text-gray-600 dark:text-gray-300 bg-teal-50 dark:bg-teal-900/30 border border-teal-100 dark:border-teal-800 rounded-xl px-3 py-2">
        {t('pin_login_help')}
      </p>

      {/* School code — remembered per device */}
      <div className="space-y-2">
        <Label htmlFor="pin-school-code" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
          <School className="w-4 h-4 text-teal-600" /> {t('pin_login_school_code')}
        </Label>
        {remembered ? (
          <div className="flex items-center justify-between h-12 px-4 border-2 border-teal-100 bg-teal-50/60 dark:bg-teal-900/20 rounded-xl">
            <span className="font-mono text-lg tracking-widest" dir="ltr" data-testid="pin-school-code-remembered">{remembered}</span>
            <button type="button" onClick={changeSchool} className="text-xs text-teal-700 hover:underline font-medium">
              {t('pin_login_change_school')}
            </button>
          </div>
        ) : (
          <>
            <Input
              id="pin-school-code"
              dir="ltr"
              inputMode="numeric"
              autoComplete="off"
              value={schoolCode}
              onChange={(e) => setSchoolCode(digitsOnly(e.target.value, 10))}
              placeholder="00000"
              className={fieldClass}
              data-testid="pin-school-code-input"
              required
            />
            <p className="text-xs text-gray-500">{t('pin_login_school_code_hint')}</p>
          </>
        )}
      </div>

      {/* Student number */}
      <div className="space-y-2">
        <Label htmlFor="pin-student-number" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
          <Hash className="w-4 h-4 text-teal-600" /> {t('pin_login_student_number')}
        </Label>
        <Input
          id="pin-student-number"
          dir="ltr"
          inputMode="numeric"
          autoComplete="off"
          value={studentNumber}
          onChange={(e) => setStudentNumber(digitsOnly(e.target.value, 12))}
          placeholder="260001"
          className={fieldClass}
          data-testid="pin-student-number-input"
          required
        />
      </div>

      {/* PIN */}
      <div className="space-y-2">
        <Label htmlFor="pin-code" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-teal-600" /> {t('pin_login_pin')}
        </Label>
        <div className="relative">
          <Input
            id="pin-code"
            dir="ltr"
            type={showPin ? 'text' : 'password'}
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(digitsOnly(e.target.value, PIN_LENGTH))}
            placeholder="••••"
            className={`${fieldClass} pe-12 text-2xl`}
            data-testid="pin-code-input"
            required
          />
          <button
            type="button"
            onClick={() => setShowPin((v) => !v)}
            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gray-600"
            aria-label={showPin ? t('pin_login_hide_pin') : t('pin_login_show_pin')}
            tabIndex={-1}
          >
            {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {lockMessage && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" role="alert">
          {lockMessage}
        </div>
      )}

      <Button
        type="submit"
        data-testid="pin-login-button"
        disabled={loading || pin.length !== PIN_LENGTH}
        className="w-full h-12 bg-gradient-to-r from-teal-800 to-teal-700 hover:from-teal-900 hover:to-teal-900 text-white font-bold text-base rounded-xl"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            {t('loading')}
          </span>
        ) : t('pin_login_submit')}
      </Button>
    </form>
  );
};

export default PinLoginForm;
